import { Navigate, useParams } from 'react-router';
import { getServiceBySlug } from '@/data/services';
import { Hero } from '@/components/sections/Hero';
import { ProcessSteps } from '@/components/sections/ProcessSteps';
import { FaqSection } from '@/components/sections/FaqSection';
import { RelatedServices } from '@/components/sections/RelatedServices';
import { CtaBlock } from '@/components/sections/CtaBlock';
import { Button } from '@/components/ui/Button';
import { SectionHeading } from '@/components/ui/SectionHeading';
import { Check } from '@/icons';
import { Branding, DesignLayout, CodeLaptop } from '@/illustrations';
import { useSeo } from '@/lib/seo';

const illustrationMap = {
  branding: Branding,
  designLayout: DesignLayout,
  codeLaptop: CodeLaptop,
};

export default function ServiceDetail() {
  const { slug } = useParams<{ slug: string }>();
  const service = slug ? getServiceBySlug(slug) : undefined;

  useSeo({
    title: service ? service.title : 'Service not found',
    description: service ? service.tagline : 'Service not found.',
  });

  if (!service) return <Navigate to="/404" replace />;
  const Illustration = illustrationMap[service.illustration];

  return (
    <>
      <Hero
        eyebrow={`Services · ${service.title}`}
        headline={service.title}
        description={service.tagline}
        actions={
          <>
            <Button to="/contact" variant="primary" withArrow>Start this project</Button>
            <Button to="/pricing" variant="secondary">See pricing</Button>
          </>
        }
        illustration={<Illustration className="w-full max-w-lg" />}
      />

      <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32">
        <div className="mx-auto max-w-[1440px] grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-24">
          <SectionHeading eyebrow="Overview" title="What you are buying." />
          <div className="prose-custom text-ink-secondary">
            {service.overview.map((para) => (
              <p key={para.slice(0, 20)}>{para}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32 bg-surface">
        <div className="mx-auto max-w-[1440px] grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-24">
          <SectionHeading eyebrow="What's included" title="No surprises at delivery." />
          <ul className="grid md:grid-cols-2 gap-x-6 gap-y-4">
            {service.included.map((item) => (
              <li key={item} className="flex gap-3 text-ink-secondary">
                <Check className="w-5 h-5 text-accent mt-0.5 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <ProcessSteps
        title="How we run it."
        description="Every step has a deliverable and a sign-off. You are never wondering where we are."
        steps={service.process}
      />

      <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32">
        <div className="mx-auto max-w-[1440px] grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-24">
          <SectionHeading eyebrow="Deliverables" title="What lands in your inbox." />
          <ul className="grid md:grid-cols-2 gap-x-6 gap-y-4">
            {service.deliverables.map((item) => (
              <li key={item} className="mono-meta text-ink-primary border-l border-accent pl-4 py-1">
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32 bg-surface">
        <div className="mx-auto max-w-[1440px] grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-24">
          <SectionHeading eyebrow="Timeline" title="How long it takes." description="Realistic estimates, not sales numbers. Weekends are not counted." />
          <ol className="flex flex-col">
            {service.timeline.map((row) => (
              <li key={row.label} className="grid grid-cols-[1fr_auto] gap-4 py-4 border-b border-border">
                <span className="text-ink-primary">{row.label}</span>
                <span className="mono-meta text-accent">{row.value}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32">
        <div className="mx-auto max-w-[1440px] grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-24">
          <SectionHeading eyebrow="What we need from you" title="Short list, big impact." description="Projects run faster when these are ready on day one." />
          <ul className="grid md:grid-cols-2 gap-x-6 gap-y-4">
            {service.clientInputs.map((item) => (
              <li key={item} className="flex gap-3 text-ink-secondary">
                <Check className="w-5 h-5 text-accent mt-0.5 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32 bg-surface">
        <div className="mx-auto max-w-[1440px] grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-24">
          <SectionHeading eyebrow="Not included" title="What we do not cover." description="To keep the quote honest, here is what is out of scope. Most can be added as a paid add-on or combined with another service." />
          <ul className="flex flex-col">
            {service.notIncluded.map((item) => (
              <li key={item} className="text-ink-muted leading-relaxed py-3 border-b border-border">{item}</li>
            ))}
          </ul>
        </div>
      </section>

      <FaqSection title={`FAQ about ${service.title.toLowerCase()}`} items={service.faq} />

      <RelatedServices excludeSlug={service.slug} />

      <CtaBlock />
    </>
  );
}
